import React, { useEffect, useState } from "react";
import Tree from "rc-tree";
import "rc-tree/assets/index.css";
import { Folder, File } from "lucide-react";
import { getDesignFolderTree } from "../../api/design";
import { X } from "lucide-react";
import { useParams } from "react-router-dom";

export default function DesignTreePicker({ open, onClose, onConfirm }) {
  const { id: projectId } = useParams();
  const [treeData, setTreeData] = useState([]);
  const [expandedKeys, setExpandedKeys] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    if (!open) return;
    const loadTree = async () => {
      try {
        const res = await getDesignFolderTree(projectId);
        const formatNode = (node) => ({
          key: node.isLeaf ? `doc-${node.id}` : `folder-${node.id}`,
          title: node.name,
          isLeaf: node.isLeaf,
          children: node.children ? node.children.map(formatNode) : [],
          raw: node,
        });
        setTreeData(res.data.map(formatNode));
      } catch (err) {
        console.error("설계도서 트리 불러오기 실패:", err);
      }
    };
    loadTree();
  }, [open, projectId]);

  if (!open) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      {/* Overlay */}
      <div className="absolute inset-0 bg-black/40" onClick={onClose} />

      {/* Dialog */}
      <div className="relative z-10 w-[500px] max-w-[95vw] h-[600px] rounded-2xl bg-white border shadow-2xl flex flex-col">
        {/* Header */}
        <div className="flex items-center justify-between px-5 py-3 border-b">
          <h3 className="text-lg font-semibold">설계도서에서 선택</h3>
          <button onClick={onClose} className="p-1 rounded hover:bg-slate-100">
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Body (트리 영역 스크롤 고정) */}
        <div className="flex-1 overflow-y-auto p-5">
          <Tree
            treeData={treeData}
            expandedKeys={expandedKeys}
            onExpand={(keys) => setExpandedKeys(keys)}
            showIcon={false}
            titleRender={(n) => (
              <div className="flex items-center gap-2">
                {n.isLeaf ? (
                  <File className="w-4 h-4 text-slate-500" />
                ) : (
                  <Folder className="w-4 h-4 text-amber-500" />
                )}
                <span className="text-sm">{n.title}</span>
              </div>
            )}
            onSelect={(keys, info) => {
              const node = info.node;
              if (node.isLeaf) {
                setSelectedFile(node.raw);
              }
            }}
          />
        </div>

        {/* Footer */}
        <div className="border-t bg-slate-50 rounded-b-2xl px-5 py-3 flex items-center justify-between">
          <div className="text-sm text-slate-700">
            {selectedFile ? (
              <>
                선택된 도면:{" "}
                <span className="font-medium text-indigo-600">
                  {selectedFile.name || selectedFile.title}
                </span>
              </>
            ) : (
              <span className="text-slate-400">선택된 도면 없음</span>
            )}
          </div>
          <div className="flex gap-2">
            <button
              onClick={onClose}
              className="rounded-lg border px-4 py-2 text-sm hover:bg-slate-100"
            >
              취소
            </button>
            <button
              onClick={() => {
                if (selectedFile) {
                  onConfirm?.(selectedFile);
                  onClose();
                }
              }}
              disabled={!selectedFile}
              className={`rounded-lg px-4 py-2 text-sm text-white shadow transition ${
                selectedFile
                  ? "bg-indigo-600 hover:bg-indigo-700"
                  : "bg-slate-300 cursor-not-allowed"
              }`}
            >
              선택
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
